/**
 * `/merchandising/orders/$orderId/edit` — correct a live Garment Order
 * (ADR-0039, ADR-0042). The composer loads the current detail and submits
 * `garmentOrders.update`. Editable while `open` or `accepted` — the buyer's
 * PO paperwork routinely lands after acceptance, and PO details are optional
 * at intake. Once a Style File is linked (or the order is closed/cancelled)
 * the frame is frozen; those get an explanatory notice, and the server
 * enforces the same rule.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { Alert, Skeleton } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { GarmentOrderComposerPage } from "../../../../features/styles/index.ts";

export const Route = createFileRoute("/(workspace)/merchandising/orders/$orderId/edit")({
  component: OrderEditRoute,
});

function OrderEditRoute() {
  const { orderId } = Route.useParams();
  const detail = trpc.garmentOrders.get.useQuery({ id: orderId });

  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Merchandising", to: "/" },
        { label: "Orders", to: "/merchandising/orders" },
        { label: "Edit order" },
      ]}
      viewportClassName="!p-0"
      contentClassName="max-w-none"
    >
      {detail.isLoading ? (
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-6">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : detail.error || !detail.data ? (
        <div className="mx-auto w-full max-w-2xl p-6">
          <Alert
            intent="bad"
            title="Couldn't load the order"
            description={detail.error?.message ?? "Order not found."}
          />
        </div>
      ) : detail.data.status !== "open" && detail.data.status !== "accepted" ? (
        <div className="mx-auto w-full max-w-2xl p-6">
          <Alert
            intent="warn"
            title="This order can no longer be edited"
            description={`${detail.data.orderNo} is ${detail.data.status}. Engineering has picked it up — corrections past this point are new records, and the order book stays append-only.`}
          />
          <p className="mt-3 text-sm text-muted">
            <Link
              to="/merchandising/orders/$orderId"
              params={{ orderId }}
              className="text-sky-700 hover:underline"
            >
              Back to the order
            </Link>
          </p>
        </div>
      ) : (
        <GarmentOrderComposerPage
          initial={{
            id: detail.data.id,
            orderNo: detail.data.orderNo,
            buyerId: detail.data.buyerId,
            description: detail.data.description,
            buyerRef: detail.data.buyerRef,
            poNumber: detail.data.poNumber,
            poDate: detail.data.poDate,
            deliveryDate: detail.data.deliveryDate,
            notes: detail.data.notes,
            items: detail.data.items.map((item) => ({
              garmentType: item.garmentType,
              name: item.name,
              spec: item.spec,
              lines: item.lines.map((line) => ({
                colour: line.colour,
                colourHex: line.colourHex,
                size: line.size,
                qty: line.qty,
              })),
            })),
          }}
        />
      )}
    </WorkspaceShell>
  );
}
